import { useSyncExternalStore } from 'react';

/**
 * How long after the last scroll event things count as standing still again.
 *
 * Long enough that a mouse wheel turned in steps stays ONE gesture — a shorter
 * window would switch back and forth between the two states with every click of
 * the wheel, which costs more than it saves and reads as flicker.
 */
const QUIET_MS = 120;

let scrolling = false;
let quietTimer: ReturnType<typeof setTimeout> | null = null;
let listening = false;
const listeners = new Set<() => void>();

function emit(): void {
    for (const listener of listeners) {
        listener();
    }
}

function settle(): void {
    quietTimer = null;
    if (scrolling) {
        scrolling = false;
        emit();
    }
}

/**
 * A grid's row viewport — the only surface whose scrolling this is about.
 *
 * Without the check, scrolling ANYTHING (a dialog's body, an Autocomplete's
 * option list, a long form) would empty the action buttons of every grid on
 * screen for a moment, none of which is moving. MUI's own class rather than a
 * marker of ours because the element belongs to the grid, not to us.
 */
const SCROLLER_CLASS = 'MuiDataGrid-virtualScroller';

function onScroll(event: Event): void {
    const target = event.target;
    if (
        !(target instanceof Element) ||
        !target.classList.contains(SCROLLER_CLASS)
    ) {
        return;
    }
    if (quietTimer) {
        clearTimeout(quietTimer);
    }
    quietTimer = setTimeout(settle, QUIET_MS);
    if (!scrolling) {
        scrolling = true;
        emit();
    }
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    if (!listening) {
        /**
         * Capture phase, on the document: a scroll event does NOT bubble, and
         * the element that scrolls here is a grid's inner viewport rather than
         * the window. One passive listener answers for every grid in the app,
         * which is also why it is never removed again — it costs nothing while
         * nobody scrolls, and re-attaching it per grid would only multiply it.
         */
        document.addEventListener('scroll', onScroll, {
            capture: true,
            passive: true
        });
        listening = true;
    }
    return () => {
        listeners.delete(listener);
    };
}

function getSnapshot(): boolean {
    return scrolling;
}

/** Nothing scrolls while the markup is being generated. */
function getServerSnapshot(): boolean {
    return false;
}

/**
 * Whether the user is scrolling ANYTHING right now.
 *
 * For cells that are expensive to build: a virtualised grid mounts and unmounts
 * its rows continuously while it moves, and roughly half the cost of scrolling
 * the Befundliste was measured to be the reference-chip cells being built and
 * torn down again. What is read in that moment is the text, not the trimmings —
 * so the trimmings can wait the fraction of a second until the list stands
 * still.
 *
 * Deliberately a store rather than context or state: subscribing through
 * `useSyncExternalStore` re-renders the CELLS that ask, and nothing above them.
 * A boolean in a provider would re-render the page around the grid on every
 * scroll, which is the opposite of the point.
 *
 * It is app-wide rather than per grid, so a SECOND grid on the same screen
 * flips it too. That is harmless by construction — a cell that is already
 * measured keeps what it has and only leaves out the parts nobody can see —
 * and it is the reason the listener ignores everything that is not a grid
 * viewport (see `SCROLLER_CLASS`).
 */
export function useIsScrolling(): boolean {
    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
